import DesktopRoutes from "@src/app/routes/DesktopRoutes";
import MobileRoutes from "@src/app/routes/MobileRoutes";
import { PrivateRoute } from "@src/app/routes/PrivateRoute";
import * as accountApi from "@src/domains/shared/apis/accountApi";
import { Roles } from "@src/domains/shared/models/Enums/Roles";
import { bootstrapAuthSession } from "@src/domains/shared/services/authService";
import { AuthState, useAuth } from "@src/domains/shared/stores/authStore";
import "@src/domains/shared/styles/custom.css";
import { lazy, Suspense, useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { isMobileDevice } from "../domains/shared/helpers/deviceHelper";

const Login = lazy(() => import("@src/domains/shared/components/Login"));
const ForgetPassword = lazy(
  () => import("@src/domains/shared/components/PasswordForget"),
);
const ResetPassword = lazy(
  () => import("@src/domains/shared/components/PasswordReset"),
);
const ForgetPasswordDesktop = lazy(
  () => import("@src/domains/shared/components/ForgetPasswordDesktop"),
);
const ForgetPasswordMobile = lazy(
  () => import("@src/domains/shared/components/ForgetPasswordMobile"),
);
const PasswordResetRegistration = lazy(
  () => import("@src/domains/shared/components/PasswordResetRegistration"),
);
const LoginDesktop = lazy(
  () => import("@src/domains/shared/components/LoginDesktop"),
);
const LoginMobile = lazy(
  () => import("@src/domains/shared/components/LoginMobile"),
);
const ResetPasswordDesktop = lazy(
  () => import("@src/domains/shared/components/ResetPasswordDesktop"),
);
const ResetPasswordMobile = lazy(
  () => import("@src/domains/shared/components/ResetPasswordMobile"),
);
const DesktopLayout = lazy(
  () => import("@src/domains/shared/layouts/DesktopLayout"),
);
const MobileLayout = lazy(
  () => import("@src/domains/shared/layouts/MobileLayout"),
);
const NotFound = lazy(() => import("@src/domains/shared/pages/NotFound"));

interface IRoleNavigation {
  readonly state: string;
  readonly to: string;
}

export const appDefaultColor = "#1976d2";

export const routesMap: Record<Roles, IRoleNavigation> = {
  [Roles.SuperAdmin]: { to: "/admin/users", state: "admin-users" },
  [Roles.CustomerAdmin]: { to: "/admin/customer-list", state: "admin-customer-list" },
  [Roles.ProcessAdmin]: { to: "/admin/users", state: "admin-users" },
  [Roles.ProcessCustomerAdmin]: { to: "/admin/customer-list", state: "admin-customer-list" },
  [Roles.ProcessTechnician]: { to: "/m/technician", state: "technician" },
  [Roles.ProcessOffice]: { to: "/office", state: "office" },
  [Roles.ProcessCustomer]: { to: "/customer", state: "customer" },
  [Roles.Service]: { to: "/services", state: "services" },
  [Roles.ServiceCustomer]: { to: "/services", state: "services" },
  [Roles.ServiceOverview]: { to: "/services", state: "services" },
  [Roles.ServiceAdmin]: {
    to: "/admin/service-catalog",
    state: "admin-service-catalog",
  },
  [Roles.InventoryAdmin]: { to: "/admin/users", state: "admin-users" },
  [Roles.InventoryManager]: { to: "/inventory", state: "inventory" },
  [Roles.InventoryRecorder]: { to: "/m/inventory", state: "inventory" },
  [Roles.InventoryAuditor]: { to: "/m/inventory", state: "inventory" },
};

export let appMode = "development";
export let appColor = "red";

if (import.meta.env.MODE === "staging") {
  appMode = "staging";
  appColor = "gray";
} else if (import.meta.env.PROD) {
  appMode = "production";
  appColor = "#1976d2";
}

export default function App() {
  const [isAuth, setIsAuth] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const { user, userRoles, setUser, setUserRoles } = useAuth(
    (state: AuthState) => state,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = isMobileDevice();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      setIsCheckingAuth(true);
      const userData = await bootstrapAuthSession();

      if (userData) {
        setUser(userData);
        setUserRoles(userData.roleNames ?? []);
        setIsAuth(true);
      } else {
        setIsAuth(false);
      }

      setIsCheckingAuth(false);
    })();
  }, [setUser, setUserRoles]);

  useEffect(() => {
    if (!isAuth || isCheckingAuth) {
      return;
    }

    (async () => {
      try {
        const userData = await accountApi.getMe();
        setUser(userData);
        setUserRoles(userData.roleNames ?? []);
      } catch {
        // Keep the session established by bootstrap/login.
      }
    })();
  }, [isAuth, isCheckingAuth, setUser, setUserRoles]);

  useEffect(() => {
    if (isCheckingAuth) {
      return;
    }

    if (isMobile) {
      if (isAuth) {
        if (
          [Roles.Service, Roles.ServiceCustomer].some(
            (r) => userRoles.includes(r) || user?.roleNames?.includes(r),
          )
        ) {
          navigate("/m/services", { replace: true });
          return;
        }

        if (
          [Roles.ProcessTechnician].some(
            (r) => userRoles.includes(r) || user?.roleNames?.includes(r),
          )
        ) {
          if (
            location.pathname.includes("login") ||
            location.pathname === "/"
          ) {
            navigate("/m/technician");
          }

          return;
        }

        if (
          [Roles.InventoryAuditor, Roles.InventoryRecorder].some(
            (r) => userRoles.includes(r) || user?.roleNames?.includes(r),
          )
        ) {
          if (
            location.pathname.includes("login") ||
            location.pathname === "/"
          ) {
            navigate("/m/inventory");
          }
          return;
        }
      }

      if (location.pathname.includes("reset-password")) {
        const token = searchParams.get("token");
        if (token) {
          navigate(`/m/reset-password?token=${token}`);
        }
        return;
      }

      if (
        !window.location.pathname.includes("/m") &&
        (window.location.pathname.includes("forget-password") ||
          window.location.pathname.includes("register-password"))
      ) {
        navigate(`/m${location.pathname}`);
        return;
      }

      if (
        window.location.pathname.includes("m/forget-password") ||
        window.location.pathname.includes("m/register-password")
      ) {
        return;
      }

      navigate("/m/login");
    } else {
      if (isAuth) {
        if (
          location.pathname.includes("login") ||
          location.pathname === "/" ||
          location.pathname === "/admin"
        ) {
          const userRoles = user?.roleNames;
          const firstRole = userRoles?.[0];
          if (firstRole) {
            const baseRole = routesMap[firstRole as Roles];
            navigate(baseRole?.to, {
              state: baseRole?.state,
              replace: true,
            });
          }
        }
        return;
      }

      if (
        location.pathname.includes("forget-password") ||
        location.pathname.includes("register-password") ||
        location.pathname.includes("reset-password")
      ) {
        return;
      }

      navigate("/login");
    }
  }, [location.pathname, isAuth, isCheckingAuth, isMobile, navigate, searchParams, user?.roleNames, userRoles]);

  if (isCheckingAuth) {
    return (
      <Suspense fallback="Laden...">
        <div>Laden...</div>
      </Suspense>
    );
  }

  return (
    <Suspense fallback="Laden...">
      <Routes>
        {/* Desktop Layout */}
        {!isMobile && isAuth && (
          <Route
            path="/"
            element={
              <PrivateRoute isAuthenticated={isAuth}>
                <DesktopLayout
                  isAuth={isAuth}
                  onValidation={(isAuthenticated) => setIsAuth(isAuthenticated)}
                />
              </PrivateRoute>
            }>
            <Route path="*" element={<DesktopRoutes />} />
          </Route>
        )}
        {!isMobile && !isAuth && (
          <>
            <Route
              path="login"
              element={
                <LoginDesktop>
                  <Login
                    onValidation={(isAuthenticated) =>
                      setIsAuth(isAuthenticated)
                    }
                  />
                </LoginDesktop>
              }
            />
            <Route
              path="forget-password"
              element={
                <ForgetPasswordDesktop>
                  <ForgetPassword />
                </ForgetPasswordDesktop>
              }
            />
            <Route
              path="reset-password"
              element={
                <ResetPasswordDesktop>
                  <ResetPassword />
                </ResetPasswordDesktop>
              }
            />

            <Route
              path="register-password"
              element={
                <LoginDesktop>
                  <PasswordResetRegistration />
                </LoginDesktop>
              }
            />
          </>
        )}
        {/* Mobile Layout */}
        {isMobile && isAuth && (
          <Route
            path="/m"
            element={
              <PrivateRoute isAuthenticated={isAuth}>
                <MobileLayout
                  isAuth={isAuth}
                  onValidation={(isAuthenticated) => setIsAuth(isAuthenticated)}
                />
              </PrivateRoute>
            }>
            <Route path="*" element={<MobileRoutes />} />
          </Route>
        )}
        {isMobile && !isAuth && (
          <Route path="/m">
            <Route
              path="forget-password"
              element={
                <ForgetPasswordMobile>
                  <ForgetPassword />
                </ForgetPasswordMobile>
              }
            />
            <Route
              path="register-password"
              element={
                <ForgetPasswordMobile>
                  <PasswordResetRegistration />
                </ForgetPasswordMobile>
              }
            />
            <Route
              path="reset-password"
              element={
                <ResetPasswordMobile>
                  <ResetPassword />
                </ResetPasswordMobile>
              }
            />
            <Route
              path="login"
              element={
                <LoginMobile>
                  <Login
                    onValidation={(isAuthenticated) =>
                      setIsAuth(isAuthenticated)
                    }
                  />
                </LoginMobile>
              }
            />
          </Route>
        )}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
